// Copyright 2024, Imprivata, Inc.  All rights reserved.

import {
  AppError,
  Authn,
  AuthnMethod,
  type AuthnViews,
  defaultFeatureFlags,
  ErrorCode,
  type EventPayload,
  EventType,
  type FactorOption,
  FatalError,
  type FeatureFlags,
  type FeatureFlagsProp,
  RetryableError,
} from '@imprivata-cloud/authn';
import {
  addSpanEvent,
  createSpanName,
  debug,
  error,
  instrument,
  SpanComponent,
  ImprHeaders,
  withAction,
} from '@imprivata-cloud/common';
import { WEB_APP_NAME } from '../../shared/constants';
import { QueryParam } from '../../constants';
import {
  getOrgPreferences,
  handleAuthnArtifact,
  setDefaultHeader,
} from '../../api/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import {
  endWorkflowAndCloseWindow,
  hideWindow,
  useAgent,
} from '../../api/agent';
import { getWorkflowId } from '../../tracing';
import {
  Card,
  type CardHeaderBannerProps,
  ScanningElement,
} from '@imprivata-cloud/components';
import {
  IGNORED_ERROR_CODES,
  IGNORED_ERROR_MESSAGES,
  mapErrorToStatus,
} from '../../utils/bannerUtils';
import { useTranslation } from 'react-i18next';
import OfflineIcon from '../../assets/offline.svg?react';
import PasswordHex from '../../assets/passwordHex.svg?react';
import DefaultLogofrom from '../../assets/default_logo.svg?react';
import PhoneHex from '../../assets/phone.svg?react';
import TempcodeHex from '../../assets/tempcodeHex.svg?react';
import { AuthnLayout, LayoutHeaders } from '../Card/authn-layout';
import { AccountLockScreen } from '../../components/account-lock-screen';
import { InformationDialog } from '../../errorHandler/error-views/no-inline-enrollment';
import { Banner } from '@imprivata-cloud/design-system';
import './IcpFlow.less';

const onSuccessSpan = createSpanName(SpanComponent.UI, 'service', 'on-success');

export const IcpFlow: React.FC = () => {
  const showUI = useAgent();
  const { t } = useTranslation();
  const [header, setHeader] = useState<LayoutHeaders>(
    LayoutHeaders.AUTHENTICATION,
  );
  const [customHexagonImage, setCustomHexagonImage] = useState<
    string | React.ReactNode
  >();
  const [banner, setBanner] = useState<CardHeaderBannerProps | undefined>();
  const [finished, setFinished] = useState(false);
  const [logoUrl, setLogoUrl] = useState<
    string | null | undefined | React.ReactNode
  >(null);
  const [errorScreen, setErrorScreen] = useState<ErrorCode | undefined>(
    undefined,
  );
  const [currentView, setCurrentView] = useState<AuthnViews>();

  const queryParams = new URLSearchParams(window.location.search);
  const applicationId = queryParams.get(QueryParam.ApplicationId) ?? undefined;
  const tenantId = queryParams.get(QueryParam.TenantId) ?? '';
  const rpRequestId = queryParams.get(QueryParam.RpRequestId) ?? '';
  const username = queryParams.get(QueryParam.LoginHint) ?? '';
  const hideAddAnotherMethod = !queryParams.get(QueryParam.ShowAddMethod);
  const contextResource = useMemo(() => {
    if (applicationId) {
      return { resourceType: '', contextType: '' };
    }
    return {
      resourceType: queryParams.get(QueryParam.ResourceType) ?? '',
      contextType: queryParams.get(QueryParam.ContextType) ?? '',
    };
  }, [applicationId, queryParams]);

  const oidcRequestData = useMemo(
    () => ({
      codeChallenge: queryParams.get(QueryParam.CodeChallenge) ?? '',
      codeChallengeMethod:
        queryParams.get(QueryParam.CodeChallengeMethod) ?? '',
    }),
    [queryParams],
  );
  const factors = useMemo(() => {
    const factors = queryParams.get(QueryParam.AcrValues);
    if (Array.isArray(factors)) {
      return factors.map(factor => ({ factorType: factor as AuthnMethod }));
    }
    return factors ? ([{ factorType: factors }] as FactorOption[]) : undefined;
  }, [queryParams]);

  // Get feature flags from URL Search Params
  const featureFlags: FeatureFlagsProp = {};
  const seachParams = new URLSearchParams(window.location.search);
  for (const key of Object.keys(defaultFeatureFlags) as Array<
    keyof FeatureFlags
  >) {
    const paramValue = seachParams.get(key as string);
    if (paramValue !== null) {
      if (typeof defaultFeatureFlags[key] === 'boolean') {
        featureFlags[key] = paramValue === 'true';

        // Uncomment if we add other types of feature flags
        //
        // } else if (typeof featureFlags[key] === 'number') {
        //   featureFlags[key] = Number.parseFloat(paramValue);
        // } else {
        //   featureFlags[key] = paramValue;
      }
    }
  }

  const deviceFactors = useMemo(() => {
    if (!factors) {
      return [
        AuthnMethod.FACE_ID,
        AuthnMethod.PASSWORD,
        AuthnMethod.IMPR_ID,
        AuthnMethod.IMPR_PIN,
        AuthnMethod.TEMP_CODE,
      ];
    }
    //we need PASSWORD factor when using "face" since we could need to ENROLL/CONSENT and that needs PASSWORD
    if (factors?.find(factor => factor.factorType === AuthnMethod.FACE_ID)) {
      return [AuthnMethod.FACE_ID, AuthnMethod.PASSWORD];
    }
    return factors.map(factor => factor.factorType);
  }, [factors]);

  const errorScreens: Partial<Record<ErrorCode, React.ReactElement>> = useMemo(
    () => ({
      [ErrorCode.ACCOUNT_LOCKED]: (
        <Card>
          <AccountLockScreen
            onRetry={() => {
              setErrorScreen(undefined);
            }}
          />
        </Card>
      ),
      [ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT]: (
        <InformationDialog
          title="Face not enrolled"
          text="Enroll your face to enable face authentication"
          buttonProps={{
            onClick: () => endWorkflowAndCloseWindow(),
            children: <>{t('commons.continue-button')}</>,
          }}
        />
      ),
      [ErrorCode.ACCOUNT_DISABLED]: (
        <Card>
          <AccountLockScreen
            statusType="disabled"
            onRetry={() => {
              setErrorScreen(undefined);
            }}
          />
        </Card>
      ),
    }),
    [t],
  );

  const requestConfig = useMemo(
    () => ({
      clientName: WEB_APP_NAME,
      username,
      oidcRequestData,
    }),
    [oidcRequestData, username],
  );

  useEffect(() => {
    setDefaultHeader(
      ImprHeaders.ImprClientName,
      jsonToBase64({
        name: WEB_APP_NAME,
      }),
    );
  }, []);

  useEffect(() => {
    setDefaultHeader(ImprHeaders.ImprTenantId, tenantId);
    if (tenantId) {
      getLogo();
    }
  }, [tenantId]);

  // scroll the page up when virtual keyboard is hidden (input looses focus)
  useEffect(() => {
    // this fixes an issue on mobile when, after virtual keyboard is hidden, the page
    // remains scrolled down a bit and there is no way to scroll it up because overall
    // container has "overflow: hidden" and there is no scroll bar
    const scrollHandler = () => {
      window.scrollTo(0, 0);
    };
    // 'focusout' - is the same as 'blur' but with the difference that it bubbles up,
    // so that it, unlike 'blur', can be catched on 'window'
    window.addEventListener('focusout', scrollHandler);
    return () => {
      window.removeEventListener('focusout', scrollHandler);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to update rpRequestId when session expires (EAM but fix)
  const onSuccess = useCallback(
    instrument(
      onSuccessSpan,
      withAction('Redirection', async (authorizationCode: string) => {
        try {
          debug('Processing authentication with code');

          const {
            redirectUri,
            authorizationCode: responseAuthorizationCode,
            state,
          } = await handleAuthnArtifact({
            rpRequestId,
            authorizationCode,
          });

          debug('Authentication artifact processed successfully', {
            redirectUri,
            hasState: !!state,
          });

          setFinished(true);
          hideWindow();

          const stateParam = state ? `&state=${state}` : '';

          addSpanEvent(createSpanName(onSuccessSpan, 'redirect'), {
            redirectUri,
            state,
          });

          debug('Redirecting to relying party', { redirectUri });

          window.location.href = `${redirectUri}?code=${responseAuthorizationCode}${stateParam}`;
        } catch (err) {
          error('Authentication failed', String(err));
          throw err; // Let the instrumentation handle the error
        }
      }),
    ),
    [rpRequestId],
  );

  const displayBanner = useCallback(
    (error: AppError) => {
      const noBanner =
        error instanceof FatalError ||
        (error.code && IGNORED_ERROR_CODES.includes(error.code)) ||
        (error.message && IGNORED_ERROR_MESSAGES.includes(error.message));

      if (noBanner) {
        setBanner(undefined);
        return;
      }

      const isOfflineError =
        error.code &&
        (error.code === ErrorCode.OFFLINE ||
          error.code === ErrorCode.NETWORK_ERROR);

      let customIcon = undefined;
      const subMessageKey = `errors.${error.code}-sub-message`;
      const subMessage = t(subMessageKey);

      if (isOfflineError) {
        customIcon = () => <OfflineIcon />;
      }

      const newBanner: CardHeaderBannerProps = {
        message: error.code ? t(`errors.${error.code}`) : t(error.message),
        status: mapErrorToStatus(error),
        customIcon,
        subMessage: subMessage !== subMessageKey ? subMessage : undefined,
      };

      setBanner(newBanner);
    },
    [t],
  );

  // Handle events coming from AuthnModule
  const handleEvent = useCallback(
    ({ event, data }: EventPayload) => {
      debug('Received event', { event, data });
      createSpanName(SpanComponent.UI, 'event', `on-event--${event}`);
      if (event === EventType.AUTHENTICATED) {
        debug('Authentication successful');
        onSuccess(data?.oidcAuthorizationCode ?? '');
      } else if (event === EventType.CANCEL) {
        debug('Authentication canceled by user');
        endWorkflowAndCloseWindow();
      }

      const authMethodIcons = {
        [AuthnMethod.FACE_ID]: <ScanningElement />,
        [AuthnMethod.PASSWORD]: <PasswordHex />,
        [AuthnMethod.IMPR_ID]: <PhoneHex />,
        [AuthnMethod.TEMP_CODE]: <TempcodeHex />,
      };

      switch (event) {
        case EventType.ERROR:
          if (
            data instanceof AppError &&
            (data.code === ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT ||
              data.code === ErrorCode.ACCOUNT_DISABLED ||
              data.code === ErrorCode.ACCOUNT_LOCKED)
          ) {
            setErrorScreen(data.code);
          } else {
            displayBanner(data);
          }

          break;
        case EventType.START_ENROLLING:
          if (data) {
            setHeader(LayoutHeaders.START_ENROLL);
          } else {
            setHeader(LayoutHeaders.ENROLL_CONSENT);
          }
          break;
        case EventType.START_AUTHENTICATING:
          setCustomHexagonImage(
            authMethodIcons?.[data as keyof typeof authMethodIcons] ??
              undefined,
          );
          break;
        case EventType.FINISH_AUTHENTICATING:
          setCustomHexagonImage(undefined);
          break;
        case EventType.ONLINE:
        case EventType.RESTART_STATE:
          setBanner(undefined);
          break;
        case EventType.OFFLINE:
          displayBanner(new RetryableError({ code: ErrorCode.OFFLINE }));
          break;
        case EventType.IMPR_ID_ERROR:
        case EventType.INTERNAL_ERROR:
          if (data) {
            if (typeof data === 'string') {
              displayBanner(
                new AppError({
                  message: data,
                  code: ErrorCode.UNKNOWN,
                }),
              );
            } else {
              displayBanner(data);
            }
          }
          break;
        case EventType.PERMISSION_REVOKED:
        case EventType.CAMERA_ERROR:
          if (data) {
            displayBanner(data);
          }
          break;
        case EventType.DEVICE_DISCONNECTED:
          if (data) {
            if (typeof data === 'string') {
              displayBanner(
                new AppError({
                  message: data,
                  code: ErrorCode.DEVICE_DISCONNECTED,
                }),
              );
            } else {
              displayBanner(data);
            }
          }
          break;
        case EventType.VIEW_CHANGED:
          setCurrentView(data);
          break;
        case EventType.PERMISSION_GRANTED:
        case EventType.DEVICE_RECONNECTED:
          setBanner(undefined);
          break;
      }
    },
    [displayBanner, onSuccess],
  );

  const tracing = useMemo(
    () => ({
      workflowId: getWorkflowId() ?? '',
    }),
    [],
  );

  const getLogo = async () => {
    try {
      const { logoUrl } = await getOrgPreferences();

      setLogoUrl(logoUrl || <DefaultLogofrom />);
    } catch (e) {
      console.log(e);
      setLogoUrl(<DefaultLogofrom />);
    }
  };

  if (finished || !showUI) {
    return <></>;
  }

  return (
    <div className="authn-container">
      {errorScreen ? (
        errorScreens[errorScreen]
      ) : (
        <AuthnLayout
          banner={banner}
          hexImage={customHexagonImage}
          header={header}
          logoUrl={logoUrl}
        >
          <Authn
            contextResource={contextResource}
            tenantId={tenantId}
            applicationId={applicationId}
            onEvent={handleEvent}
            deviceFactors={deviceFactors}
            factors={factors}
            requestConfig={requestConfig}
            tracing={tracing}
            hideAddAnotherMethod={hideAddAnotherMethod}
            featureFlags={featureFlags}
          />
        </AuthnLayout>
      )}
    </div>
  );
};
